// Helpers for mapping between a deal's owner User objects (backend) and the
// display names the owner UI works with, plus the reverse (names → member ids)
// for the write path. Owners are shown/edited as names in the grid + detail
// sheet, but the backend takes ids — these keep the two representations aligned.
// Resolving through the workspace-member list (by id) is important: the owner
// picker dedupes by name, so a name that doesn't match the member list would let
// an existing owner reappear as addable.

export interface OwnerUser {
  id: string;
  givenName?: string | null;
  surname?: string | null;
  preferredName?: string | null;
  email?: string | null;
}

export interface OwnerMemberLike {
  id: string;
  name: string;
}

/** Resolve a single owner User to a display name, preferring the member list. */
export function resolveOwnerDisplayName(
  user: OwnerUser | null | undefined,
  members: OwnerMemberLike[],
): string {
  if (!user) return '';
  const member = members.find(m => m.id === user.id);
  return (
    member?.name ||
    user.preferredName ||
    `${user.givenName || ''} ${user.surname || ''}`.trim() ||
    user.email ||
    ''
  );
}

/** Resolve a deal's additional owners to display names, dropping any that resolve empty. */
export function resolveAdditionalOwnerNames(
  additionalOwners: ReadonlyArray<OwnerUser | null | undefined> | null | undefined,
  members: OwnerMemberLike[],
): string[] {
  return (additionalOwners || [])
    .map(u => resolveOwnerDisplayName(u, members))
    .filter((name): name is string => Boolean(name));
}

/** Map owner display names → workspace member ids. Names not in the workspace are dropped. */
export function mapOwnerNamesToIds(names: string[], members: OwnerMemberLike[]): string[] {
  return names
    .map(name => members.find(m => m.name === name)?.id)
    .filter((id): id is string => Boolean(id));
}
